import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchSurveys } from '../../surveys/surveysApi';
import type { Survey } from '../../surveys/types';
import type {
  WorkerConsultationMechanism,
  WorkerConsultationPayload,
  WorkerConsultationStatus,
} from './types';
import { MECHANISM_LABEL, STATUS_LABEL } from './consultationUtils';

const MECHANISMS = Object.keys(MECHANISM_LABEL) as WorkerConsultationMechanism[];
const STATUSES = Object.keys(STATUS_LABEL) as WorkerConsultationStatus[];

interface ConsultationFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: WorkerConsultationPayload) => Promise<void>;
  initial?: Partial<WorkerConsultationPayload & { status?: WorkerConsultationStatus }>;
  title: string;
}

function toDateInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultPayload(): WorkerConsultationPayload {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  return {
    topic: '',
    mechanism: 'reunion',
    date,
    outcome: '',
    participants: '',
    linkedSurveyId: null,
    status: 'planificado',
  };
}

export function ConsultationFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  title,
}: ConsultationFormModalProps) {
  const [form, setForm] = useState<WorkerConsultationPayload>(defaultPayload());
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [surveysError, setSurveysError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = defaultPayload();
    setForm({
      ...base,
      ...initial,
      date: toDateInput(initial?.date) || base.date,
      linkedSurveyId: initial?.linkedSurveyId ?? null,
      status: initial?.status ?? base.status,
    });
    setError(null);
    setSurveysError(null);

    void (async () => {
      try {
        const result = await fetchSurveys({
          category: 'survey',
          pageSize: 100,
          active: true,
          sortBy: 'title',
          sortDir: 'asc',
        });
        setSurveys(result.data);
      } catch {
        setSurveys([]);
        setSurveysError('No se pudieron cargar las encuestas disponibles.');
      }
    })();
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        ...form,
        participants: form.participants?.trim() ? form.participants.trim() : null,
        linkedSurveyId: form.linkedSurveyId || null,
      });
      onClose();
    } catch {
      setError('No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-card p-6 shadow-lg border border-border max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="consultation-topic">
              Tema de consulta
            </label>
            <input
              id="consultation-topic"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={form.topic}
              onChange={(e) => setForm((prev) => ({ ...prev, topic: e.target.value }))}
              required
              maxLength={255}
              placeholder="Ej: Riesgos en nueva línea de producción"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="consultation-mechanism">
                Mecanismo
              </label>
              <select
                id="consultation-mechanism"
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                value={form.mechanism}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    mechanism: e.target.value as WorkerConsultationMechanism,
                  }))
                }
              >
                {MECHANISMS.map((value) => (
                  <option key={value} value={value}>
                    {MECHANISM_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="consultation-date">
                Fecha
              </label>
              <input
                id="consultation-date"
                type="date"
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                value={form.date}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="consultation-participants">
              Participantes (opcional)
            </label>
            <textarea
              id="consultation-participants"
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[72px]"
              value={form.participants ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, participants: e.target.value }))}
              maxLength={10000}
              placeholder="Representantes, áreas, cantidad de asistentes..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="consultation-outcome">
              Resultado / evidencia
            </label>
            <textarea
              id="consultation-outcome"
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[96px]"
              value={form.outcome}
              onChange={(e) => setForm((prev) => ({ ...prev, outcome: e.target.value }))}
              required
              maxLength={10000}
              placeholder="Acuerdos, compromisos y evidencia de la consulta…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="consultation-survey">
              Encuesta vinculada (opcional)
            </label>
            <select
              id="consultation-survey"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={form.linkedSurveyId ?? ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  linkedSurveyId: e.target.value || null,
                }))
              }
            >
              <option value="">Sin encuesta vinculada</option>
              {surveys.map((survey) => (
                <option key={survey.id} value={survey.id}>
                  {survey.title}
                </option>
              ))}
            </select>
            {surveysError && <p className="text-xs text-muted-foreground mt-1">{surveysError}</p>}
            {form.linkedSurveyId && (
              <p className="text-xs mt-1">
                <Link
                  to={`/surveys/${form.linkedSurveyId}/statistics`}
                  className="text-primary hover:underline"
                >
                  Ver estadísticas de la encuesta
                </Link>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="consultation-status">
              Estado
            </label>
            <select
              id="consultation-status"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={form.status ?? 'planificado'}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  status: e.target.value as WorkerConsultationStatus,
                }))
              }
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABEL[value]}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
